import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from 'components/common/Loader';
import { projectUserRole, showDropdown, Project } from 'redux/projectSlice';
import { useGetProjectByIdMutation } from 'redux/services/project';
import Modal from 'components/common/Modal/Modal';
import { ProjectDetails } from '../ConductInspection/components/ProjectDetails';
import { AddObservation } from './components/AddObservation';
import { useGetChecklist } from 'hooks/useGetChecklist';
import {
  ISingleObservation,
  useCreateObservationMutation,
  useGetObservationByIdMutation,
  useUpdateAddObservationPatchMutation,
} from 'redux/services/observation';
import {
  getInspectionDataForApi,
  getProjectDetailsFromObservationData,
} from 'pages/utils';
import toast from 'react-hot-toast';
import { setProjectDetails } from 'redux/conductInspectionSlice';
import './LogObservation.scss';
import { useTranslation } from 'react-i18next';

const LogObservation = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [obs, setObservations] = useState<ISingleObservation[]>([]);
  const [getProjectById, { data, isLoading }] = useGetProjectByIdMutation();
  const [createObservation, { isLoading: createObservationLoading }] =
    useCreateObservationMutation();
  const [addObservation, { isLoading: addObLoading }] =
    useUpdateAddObservationPatchMutation();
  const [
    getObservation,
    { data: observation, isLoading: isObservationsLoading },
  ] = useGetObservationByIdMutation();
  const { projectId, observationId, step = '0' } = useParams();
  const [newIncidentId, setNewIncidentId] = useState('');
  const [project, setProject] = useState<Project | null>(null);
  const navigate = useNavigate();
  useGetChecklist();

  useEffect(() => {
    return () => {
      dispatch(setProjectDetails(null));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!data) {
      getProjectById(projectId);
    }
  }, [data, getProjectById, projectId]);

  useEffect(() => {
    dispatch(showDropdown(true));
    if (data) {
      dispatch(projectUserRole(data.role));
      setProject(data);
    }
    return () => {
      dispatch(showDropdown(false));
    };
  }, [data, dispatch]);

  const fetchObservation = useCallback(async () => {
    await getObservation({
      projectId,
      id: observationId,
    });
  }, [getObservation, observationId, projectId]);

  useEffect(() => {
    if (observationId && !observation) {
      fetchObservation();
    }
  }, [fetchObservation, observationId, observation]);

  useEffect(() => {
    if (project && observation) {
      dispatch(
        setProjectDetails(
          getProjectDetailsFromObservationData(observation, project),
        ),
      );

      setObservations(observation.observations);
    }
  }, [project, observation, dispatch]);

  const renderPage = () => {
    switch (step) {
      case '1':
        return (
          <AddObservation
            observations={obs}
            onSubmit={handleSubmitAddObservation}
          />
        );
      default:
        return (
          <ProjectDetails
            project={project!}
            observation
            onSubmit={handleProjectDetailsSubmit}
          />
        );
    }
  };

  const handleProjectDetailsSubmit = async (values: any) => {
    const payload = getInspectionDataForApi({
      ...values,
      projectId,
    });
    const res: any = await (observationId
      ? createObservation({ ...payload, _id: observationId })
      : createObservation(payload));
    if (res.data) {
      toast.success(t('save_success'));
      navigate(`/project/${projectId}/log-observation/${res.data._id}/1`);
    } else {
      toast.error(res.error?.data?.message ?? t('something_went_wrong'));
    }
  };

  const handleSubmitAddObservation = async (payload: any) => {
    const res: any = await addObservation(payload);

    if (res.data) {
      toast.success(t('create_observation_success'));
      dispatch(setProjectDetails(null));
      setNewIncidentId(res.data.displayId! ?? observationId);
    } else {
      toast.error(res.error?.data?.message ?? t('something_went_wrong'));
    }
  };

  const handleSubmit = () => {
    setNewIncidentId('');
    navigate(`/project/${projectId}/observation`);
  };

  return (
    <div className="log-observation d-flex">
      <div className="page-title">
        <h2 className="page-title">{t('header_log_observation')}</h2>
      </div>
      <div className="d-flex flex-column setup-container">
        {project && renderPage()}
        {(isLoading ||
          createObservationLoading ||
          addObLoading ||
          isObservationsLoading) && <Loader />}
      </div>
      <Modal
        show={newIncidentId.length > 0}
        withIcon
        type={'success'}
        title=""
        modalClassName="checklist-modal"
        footerContentAlignment="center"
        withIconBodyText={
          <div className="d-flex flex-column">
            <span>
              <b>{t('successfull')}</b>
            </span>
            <span className="h6">{t('observation_id')}</span>
            <span className="h6">{observation?.displayId}</span>
          </div>
        }
        submitBtnText={t('btn_ok')}
        displayCloseButton={false}
        hideCancelBtn
        onSubmit={handleSubmit}
        onClose={() => {}}
      />
    </div>
  );
};

export default LogObservation;
